import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBBtn,
  MDBInput,
} from "mdb-react-ui-kit";
import { useMessageBox } from "../components/Common/MessageBox";

const BASE_URL =
  "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net";

const safeParse = (val) => {
  if (!val) return null;
  if (typeof val === "object") return val;
  try {
    return JSON.parse(val);
  } catch {
    return val;
  }
};

// Marketplace ki raw DB row ko readable fields mein convert karo
const buildMarketplaces = (product, ebay, shopify, vinted, poshmark) => {
  const result = {};

  // Local
  result.local = {
    Title: product.title || "",
    Description: product.description || "",
    SKU: product.sku || "",
    Price: product.price || "",
    Quantity: product.quantity || 0,
    Category: product.category || "Other",
    Condition: product.condition || "",
  };

  // eBay
  if (ebay) {
    const ebayFields = {};
    if (ebay.aspects) {
      const parsed = safeParse(ebay.aspects);
      if (parsed && typeof parsed === "object") {
        Object.entries(parsed).forEach(([k, v]) => {
          const val = Array.isArray(v) ? v.join(", ") : v;
          if (val && val !== "N/A") ebayFields[k] = val;
        });
      }
    }
    ebayFields["eBay Item ID"] = ebay.ebay_item_id || "";
    ebayFields.SKU = ebay.sku || "";
    ebayFields.Price = ebay.price || "";
    ebayFields.Quantity = ebay.quantity || 0;
    ebayFields.Condition = safeParse(ebay.condition) || "";
    ebayFields["Category ID"] = ebay.category_id || "";
    ebayFields.Currency = ebay.currency || "";
    result.ebay = ebayFields;
  }

  // Shopify
  if (shopify) {
    result.shopify = {
      "Shopify Product ID": shopify.shopify_product_id || "",
      "Variant ID": shopify.shopify_variant_id || "",
      SKU: shopify.sku || "",
      Price: shopify.price || "",
      Quantity: shopify.quantity || 0,
      Vendor: shopify.vendor || "",
      "Product Type": shopify.product_type || "",
      Status: shopify.status || "",
    };
  }

  // Vinted
  if (vinted) {
    result.vinted = {
      Title: vinted.title || "",
      Description: vinted.description || "",
      Price: vinted.price || "",
      Category: vinted.category || "",
      Brand: vinted.brand || "",
      Size: vinted.size || "",
      Condition: vinted.condition || "",
      "Package Size": vinted.package_size || "",
      Color: vinted.color || "",
    };
  }

  // Poshmark
  if (poshmark) {
    result.poshmark = {
      Title: poshmark.title || "",
      Description: poshmark.description || "",
      Category: poshmark.category || "",
      Price: poshmark.price || "",
      Size: poshmark.size || "",
      Brand: poshmark.brand || "",
      Color: poshmark.color || "",
      Condition: poshmark.condition || "",
      Quantity: poshmark.quantity || 0,
      Availability: poshmark.availability || "",
    };
  }

  return result;
};

const MARKETPLACE_COLORS = {
  local: "#6c757d",
  ebay: "#e53238",
  shopify: "#96bf48",
  vinted: "#09b1ba",
  poshmark: "#7b2d8b",
};

const MARKETPLACE_LABELS = {
  local: "General Info",
  ebay: "eBay",
  shopify: "Shopify",
  vinted: "Vinted",
  poshmark: "Poshmark",
};

const ProductDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError, MessageBoxComponent } = useMessageBox();

  const [marketplaces, setMarketplaces] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("local");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);

        // Hamesha API se fetch karo
        const token = localStorage.getItem("token");
        const res = await fetch(`${BASE_URL}/api/product-full/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();

        if (!json.success || !json.data) {
          showError("Product not found");
          return;
        }

        const { product, ebay, shopify, vinted, poshmark } = json.data;
        const mp = buildMarketplaces(product, ebay, shopify, vinted, poshmark);
        setMarketplaces(mp);
        setImages(product.images || []);
        setActiveTab(Object.keys(mp)[0] || "local");
      } catch (err) {
        console.error(err);
        showError("Failed to load product");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFieldChange = (market, key, value) => {
    setMarketplaces((prev) => ({
      ...prev,
      [market]: { ...prev[market], [key]: value },
    }));
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <div className="spinner-border text-danger" role="status" />
        <p className="mt-3">Loading product details...</p>
      </div>
    );
  }

  if (!marketplaces) {
    return (
      <div className="text-center mt-5">
        <p>No product data found.</p>
        <button className="btn btn-danger mt-3" onClick={() => navigate(-1)}>
          Go Back
        </button>
      </div>
    );
  }

  const availableTabs = Object.keys(marketplaces);

  return (
    <MDBContainer className="py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="fw-bold">Product Details</h3>
        <MDBBtn color="danger" outline onClick={() => navigate(-1)}>
          ← Back
        </MDBBtn>
      </div>

      {/* Images */}
      {images.length > 0 && (
        <div className="mb-4 d-flex gap-2 flex-wrap">
          {images.map((img, i) => (
            <img
              key={i}
              src={img}
              alt={`Product ${i + 1}`}
              style={{
                width: 100,
                height: 100,
                objectFit: "cover",
                borderRadius: 8,
                border: "1px solid #dee2e6",
              }}
            />
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="d-flex gap-2 mb-4 flex-wrap">
        {availableTabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: "8px 20px",
              borderRadius: 20,
              border: `2px solid ${MARKETPLACE_COLORS[tab] || "#dc3545"}`,
              background:
                activeTab === tab
                  ? MARKETPLACE_COLORS[tab] || "#dc3545"
                  : "white",
              color:
                activeTab === tab
                  ? "white"
                  : MARKETPLACE_COLORS[tab] || "#dc3545",
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.2s",
            }}
          >
            {MARKETPLACE_LABELS[tab] || tab}
          </button>
        ))}
      </div>

      {/* Active Tab Fields */}
      {availableTabs.map((market) => (
        <div
          key={market}
          style={{ display: activeTab === market ? "block" : "none" }}
        >
          <div
            className="p-4 rounded shadow-sm mb-4"
            style={{
              background: "#fff",
              borderLeft: `4px solid ${MARKETPLACE_COLORS[market] || "#dc3545"}`,
            }}
          >
            <h5
              className="mb-4 fw-bold"
              style={{ color: MARKETPLACE_COLORS[market] || "#dc3545" }}
            >
              {MARKETPLACE_LABELS[market] || market} Details
            </h5>
            <MDBRow>
              {Object.entries(marketplaces[market] || {}).map(
                ([key, value]) => (
                  <MDBCol md="6" lg="4" className="mb-3" key={key}>
                    <MDBInput
                      label={key}
                      value={String(value ?? "")}
                      onChange={(e) =>
                        handleFieldChange(market, key, e.target.value)
                      }
                    />
                  </MDBCol>
                ),
              )}
            </MDBRow>
          </div>
        </div>
      ))}

      {/* Actions */}
      <div className="d-flex justify-content-end gap-2 mt-2">
        <MDBBtn color="danger" onClick={() => showSuccess("Changes saved!")}>
          Save Changes
        </MDBBtn>
        <MDBBtn color="danger" outline onClick={() => navigate(-1)}>
          Cancel
        </MDBBtn>
      </div>

      <MessageBoxComponent />
    </MDBContainer>
  );
};

export default ProductDetailsPage;
